import * as Sentry from '@sentry/react-router';
import type { BrowserOptions, Span } from '@sentry/react-router';

let enabled = false;

/**
 * Mutable handle for a widget render span (NAV-8747). `ended` guards against
 * double-ending — the render span can be closed either by the "data settled"
 * signal (see useWidgetPerformance) or by the unmount cleanup, whichever
 * fires first.
 */
export interface WidgetSpan {
    span: Span;
    ended: boolean;
}

const performanceTracking = {
    init(options: BrowserOptions): void {
        if (!options.dsn) return;
        Sentry.init(options);
        enabled = true;
    },

    trackPageLoad(page: string): void {
        if (!enabled) return;

        Sentry.captureEvent({
            message: 'page_load',
            level: 'info',
            tags: { page, event_type: 'page_performance' },
        });
    },

    /**
     * Starts a span covering a widget's render lifecycle (mount → critical
     * data settled). Returns `null` when Sentry isn't initialised — callers
     * must tolerate a `null` spanRef (see endWidgetRender/trackWidgetApiCall).
     */
    startWidgetRender(widgetId: string, widgetType: string): WidgetSpan | null {
        if (!enabled) return null;

        const span = Sentry.startInactiveSpan({
            name: `widget_render.${widgetType}`,
            op: 'widget.render',
            // Own transaction in the Sentry UI — mirrors a page load, but
            // scoped to a single widget instance (NAV-8747 empirical goal).
            forceTransaction: true,
            attributes: {
                widget_id: widgetId,
                widget_type: widgetType,
            },
        });

        return { span, ended: false };
    },

    /** Ends the render span. Safe to call more than once (no-op after the first). */
    endWidgetRender(spanRef: WidgetSpan | null): void {
        if (!spanRef || spanRef.ended) return;
        spanRef.span.end();
        // eslint-disable-next-line no-param-reassign
        spanRef.ended = true;
    },

    /**
     * Runs `promiseFn`, wrapping it in a child span of the widget's render
     * span (named after the API call). The child span always ends in the
     * `finally` — success or error — and the original promise result/error
     * propagates unchanged.
     */
    trackWidgetApiCall<T>(
        spanRef: WidgetSpan | null,
        name: string,
        promiseFn: () => Promise<T>
    ): Promise<T> {
        if (!enabled || !spanRef) return promiseFn();

        const childSpan = Sentry.startInactiveSpan({
            name,
            op: 'widget.api_call',
            parentSpan: spanRef.span,
        });

        return promiseFn().finally(() => childSpan.end());
    },
};

export default performanceTracking;
